import React, { useEffect, useState } from "react";
import { Button, Row, Input, FormFeedback } from "reactstrap";
import styles from "./styles.scss";
import { serviceCaller } from "../../../composition";
import withStyles from "isomorphic-style-loader/withStyles";
import { firebaseConnect } from "react-redux-firebase";
import { connect } from "react-redux";
import {
  virtuousConnect,
  virtuousGetTagsList,
  virtuousStopSync,
} from "../../../../utils/fetch";
import {
  FirstStepProps,
  SecondStepProps,
  Props,
  Tag,
  ThirdStepProps,
  ConnectionOverviewProps,
} from "./types";
import { Step } from "../common/progressSteps/types";
import ProgressSteps from "../common/progressSteps";
import SegmentsPills from "../common/segmentsPills";
import { parseSegments } from "../common/segmentsPills/types";
import { Segment } from "../common/segmentsPills/types";
import ReviewCard from "../common/reviewCard";
import ProgressBar from "../common/progressBar";
import SuccessScreen from "../common/successScreen";
import ApiKeyInput from "../common/apiKeyInput";
import { PulseLoader } from "react-spinners";
import { maskApiKey } from "../donorPerfect/utils";

const steps: Array<Step> = [
  { label: "Enter API Key", completed: true },
  { label: "Set Tags as Segments", completed: false },
  { label: "Start Syncing", completed: false },
];

const ConnectionOverview = ({
  apiKey,
  onStopSync,
  isLoading,
  selectedTags,
}: ConnectionOverviewProps) => {
  return (
    <div>
      <p>
        Your Virtuous account is already connected to ViewSpark. If you want to
        manage your sync settings, please click "Stop sync" and run the sync
        process again.
      </p>
      <ReviewCard title="Virtuous API Key" value={maskApiKey(apiKey)} />
      <ReviewCard title="Selected Tags" value={selectedTags} />
      <Input type="hidden" name="apiKey" value={apiKey} valid />
      <FormFeedback
        className={`${styles.formFeedback} ${styles.successColor}`}
        valid
      >
        <span>
          <i className={`fa fa-check-circle`} /> Successfully connected.
        </span>
      </FormFeedback>
      <Button
        className={styles.button}
        style={{ marginTop: "-12px" }}
        onClick={!isLoading && onStopSync}
        disabled={isLoading}
      >
        {isLoading ? (
          <PulseLoader color="#1F65B1" loading={true} />
        ) : (
          "Stop Sync"
        )}
      </Button>
    </div>
  );
};

const ThirdStep = ({ apiKey, selectedSegments, onEdit }: ThirdStepProps) => {
  const segmentNames = selectedSegments.map(segment => segment.name);

  return (
    <div>
      <p>
        Review your selections below and click "Start Sync" to begin importing
        your donor data from Virtuous to ViewSpark.
      </p>
      <ReviewCard title="Virtuous API Key" value={maskApiKey(apiKey)} />
      <ReviewCard
        title="Segment Tags"
        value={segmentNames.join(", ")}
        onEdit={onEdit}
        showAll
      />
    </div>
  );
};

const SecondStep = ({
  segments,
  allowEdit,
  selectedSegments,
  setSelectedSegments,
  setCanAdvance,
}: SecondStepProps) => {
  useEffect(() => {
    if (selectedSegments.length === 0) {
      setCanAdvance(false);
    } else {
      setCanAdvance(true);
    }
  }, [selectedSegments]);

  return (
    <div>
      <p>
        Select the tags you'd like to import as segments to categorize and
        organize them effectively in ViewSpark.
      </p>
      <p style={{ fontWeight: "bold" }}>Select segment tags for donors</p>
      <SegmentsPills
        segments={segments}
        selectedSegments={selectedSegments}
        setSelectedSegments={setSelectedSegments}
        allowEdit={allowEdit}
      />
    </div>
  );
};

const FirstStep = ({
  setApiKey,
  setTags,
  isConnected,
  setIsConnected,
  currentApiKey,
  charityId,
  integrationsServiceUrl,
  serviceAccessToken,
}: FirstStepProps) => {
  const [apiKeyValue, setApiKeyValue] = useState<string>(currentApiKey);
  const [isValid, setIsValid] = useState<boolean>(
    currentApiKey && isConnected ? true : undefined
  );
  const [canConnect, setCanConnect] = useState<boolean>(!!currentApiKey);
  const [feedback, setFeedback] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const connectKey = () => {
    setIsLoading(true);
    virtuousGetTagsList(
      integrationsServiceUrl,
      charityId,
      apiKeyValue,
      serviceAccessToken
    )
      .then(response => {
        if (!response.message) {
          setApiKey(apiKeyValue);
          setIsValid(true);
          setCanConnect(false);
          setIsConnected(true);
          setIsLoading(false);
          setTags(response);
        } else {
          setIsValid(false);
          setIsLoading(false);
          setFeedback(response.message);
        }
      })
      .catch(err => {
        setIsValid(false);
        setIsLoading(false);
        setFeedback(err.message);
      });
  };

  return (
    <div>
      <p>
        Enter your Virtuous API Key below to connect to your account. If you
        don't have an API Key, please refer to the Virtuous documentation on how
        to generate one.
      </p>
      <p style={{ fontWeight: "bold" }}>Virtuous API Key</p>
      <ApiKeyInput
        setApiKeyValue={setApiKeyValue}
        setCanConnect={setCanConnect}
        setIsValid={setIsValid}
        setFeedback={setFeedback}
        onConnectKey={connectKey}
        feedback={feedback}
        isValid={isValid}
        isConnected={isConnected}
        isLoading={isLoading}
        apiKeyValue={apiKeyValue}
        canConnect={canConnect}
      />
    </div>
  );
};

const Virtuous = (props: Props) => {
  const [currentStep, setCurrentStep] = useState<number>(
    props.isConnected ? 5 : props.isConnecting ? 4 : 1
  );
  const [canAdvance, setCanAdvance] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>(props.connectedApiKey);
  const [tags, setTags] = useState<Array<Tag>>([]);
  const [segments, setSegments] = useState<Array<Segment>>([]);
  const [selectedSegments, setSelectedSegments] = useState<Array<Segment>>([]);
  const [connectionProgress, setConnectionProgress] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(props.isConnected);
  const [isStoppingSync, setIsStoppingSync] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [errorRequestingSync, setErrorRequestingSync] = useState<string>(
    undefined
  );

  useEffect(() => {
    if (!props.isConnecting && props.isConnected) {
      setCurrentStep(5);
    } else if (props.isConnecting && !props.isConnected) {
      setCurrentStep(4);
    }
  }, [props.isConnecting, props.isConnected]);

  useEffect(() => {
    const segments = parseSegments(tags);
    setSegments(segments);
    setSelectedSegments(segments);
  }, [tags]);

  useEffect(() => {
    if (apiKey === "" || tags.length === 0) return;

    setCanAdvance(true);
  }, [apiKey, tags]);

  const updateStep = (step: number) => {
    setCurrentStep(step);
    setCanAdvance(false);
    if (step === 3) setCanAdvance(true);
  };

  const goBack = () => {
    currentStep !== 1 && currentStep !== 5
      ? setCurrentStep(currentStep - 1)
      : props.onBack();
  };

  const stopSync = () => {
    setIsStoppingSync(true);
    virtuousStopSync(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      apiKey,
      props.webhookId,
      props.serviceAccessToken
    )
      .then(() => {
        goBack();
      })
      .catch(err => {
        alert("Error stopping sync: " + err);
        setIsStoppingSync(false);
      });
  };

  const startSync = () => {
    setIsSyncing(true);
    setCanAdvance(false);
    setErrorRequestingSync(undefined);
    virtuousConnect(
      props.config.integrationsServiceUrl,
      props.currentCharity.id,
      apiKey,
      selectedSegments,
      props.serviceAccessToken
    )
      .then(response => {
        setIsSyncing(false);
        if (response.status !== 200) {
          setErrorRequestingSync(
            "There was an error requesting the sync. Try again later."
          );
          setCanAdvance(true);
          return;
        }

        setCurrentStep(4);
      })
      .catch(err => {
        setIsSyncing(false);
        setCanAdvance(true);
        setErrorRequestingSync(
          "There was an error requesting the sync. Try again later."
        );
        console.error(err);
      });
  };

  const onClickNext = () => {
    if (currentStep === 3) {
      startSync();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  useEffect(() => {
    if (errorRequestingSync) setErrorRequestingSync(undefined);
  }, [currentStep]);

  return (
    <main>
      <div>
        {connectionProgress === 100 ? (
          <div>
            <SuccessScreen
              source="Virtuous"
              goBack={() => {
                setConnectionProgress(0);
                updateStep(5);
              }}
            />
          </div>
        ) : (
          <div>
            <h1 className={styles.h1}>Connect Virtuous to ViewSpark</h1>
            <p className={styles.subtitle}>
              If you wish to prevent certain donors from syncing, create the
              NO_VIEWSPARK tag and add it to their contact profiles.
            </p>
            <ProgressSteps currentStep={currentStep} stepsList={steps} />
            <div className={styles.stepContainer}>
              {currentStep === 1 && (
                <FirstStep
                  setApiKey={setApiKey}
                  currentApiKey={apiKey !== "" ? apiKey : undefined}
                  setTags={setTags}
                  isConnected={isConnected}
                  setIsConnected={setIsConnected}
                  charityId={props.currentCharity.id}
                  integrationsServiceUrl={props.config.integrationsServiceUrl}
                  serviceAccessToken={props.serviceAccessToken}
                />
              )}
              {currentStep === 2 && (
                <SecondStep
                  segments={segments}
                  allowEdit={true}
                  selectedSegments={selectedSegments}
                  setSelectedSegments={setSelectedSegments}
                  setCanAdvance={setCanAdvance}
                />
              )}
              {currentStep === 3 && (
                <div>
                  <ThirdStep
                    apiKey={apiKey}
                    selectedSegments={selectedSegments}
                    onEdit={goBack}
                  />
                  {errorRequestingSync && (
                    <span className={styles.syncFeedback}>
                      <i className={`fa fa-close`} /> {errorRequestingSync}
                    </span>
                  )}
                </div>
              )}
              {currentStep === 4 && (
                <ProgressBar currentValue={connectionProgress} />
              )}
              {currentStep === 5 && (
                <ConnectionOverview
                  apiKey={apiKey}
                  onStopSync={stopSync}
                  isLoading={isStoppingSync}
                  selectedTags={props.selectedTags
                    .map(type => type.name)
                    .join(", ")}
                />
              )}

              <Row className={styles.actionsContainer}>
                <Button
                  className={styles.button}
                  onClick={goBack}
                  disabled={currentStep === 4}
                >
                  {currentStep === 4 ? "Stop" : "Back"}
                </Button>
                {currentStep < 4 && !props.isConnected && (
                  <Button
                    className={`${styles.button} ${
                      canAdvance ? styles.nextButton : null
                    }`}
                    onClick={onClickNext}
                    disabled={!canAdvance}
                  >
                    {currentStep === 3 ? (
                      !isSyncing ? (
                        "Start Sync"
                      ) : (
                        <PulseLoader color="#fff" loading={true} />
                      )
                    ) : (
                      "Next"
                    )}
                  </Button>
                )}
              </Row>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default serviceCaller(
  connect()(firebaseConnect(null)(withStyles(styles)(Virtuous)))
);
